/**
 * Los formatos en línea del grupo conformado, por su nombre.
 *
 * Hasta ahora el grupo diligenciaba dos: la carta de conformación y la
 * declaración juramentada de residencia, que repetían casi el mismo texto y
 * las mismas firmas. De ahora en adelante van en uno solo, la "Declaración de
 * residencia y conformación del grupo", que conserva el texto de la
 * declaración y cambia el título para nombrar también la conformación.
 *
 * Los dos viejos se siguen reconociendo: lo ya diligenciado se consulta,
 * se descarga y se subsana igual que antes. Lo que cambia es cuál se le pide
 * a quien empieza, y eso lo decide el catálogo de documentos de cada
 * convocatoria, no este archivo.
 *
 * Los nombres se comparan normalizados porque vienen del catálogo, donde los
 * escribe una persona: una tilde o una mayúscula de diferencia no deberían
 * dejar un documento sin su formulario.
 */

/** El nombre que tiene la declaración cuando no incluye la conformación. */
export const DECLARACION_RESIDENCIA = 'Declaración de Residencia';

/** La carta que se diligenciaba aparte. Ya no se pide, pero existe guardada. */
export const CARTA_CONFORMACION = 'Carta de conformación de grupo';

export const RELACION_PARTICIPANTES = 'Relación de participantes de la propuesta';

/** El unificado: el que se usa de ahora en adelante. */
export const DECLARACION_RESIDENCIA_Y_CONFORMACION =
  'Declaración de residencia y conformación del grupo';

/** Sin tildes, sin mayúsculas y sin espacios de más. */
const normalizar = (valor: any): string =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** El nombre del documento, venga suelto o dentro del documento. */
const nombreDe = (documento: any): string =>
  typeof documento === 'string' ? documento : String(documento?.descripcion ?? '');

const seLlama = (documento: any, nombre: string): boolean =>
  normalizar(nombreDe(documento)) === normalizar(nombre);

/** El formato unificado, el que se pide de ahora en adelante. */
export const esDeclaracionUnificada = (documento: any): boolean =>
  seLlama(documento, DECLARACION_RESIDENCIA_Y_CONFORMACION);

/** La declaración de residencia con su nombre anterior. */
export const esDeclaracionDeResidencia = (documento: any): boolean =>
  seLlama(documento, DECLARACION_RESIDENCIA);

/** La carta de conformación, que ya no se pide pero sigue guardada. */
export const esCartaDeConformacion = (documento: any): boolean =>
  seLlama(documento, CARTA_CONFORMACION);

export const esRelacionDeParticipantes = (documento: any): boolean =>
  seLlama(documento, RELACION_PARTICIPANTES);

/**
 * ¿Va por el formato de residencia del grupo, el que firman todos?
 *
 * El unificado siempre; el viejo solo cuando ya tiene participantes, que es
 * lo que lo distingue de la declaración individual de persona natural, que
 * se llama igual y la firma una sola persona.
 */
export const esDeclaracionDeGrupo = (documento: any): boolean => {
  if (esDeclaracionUnificada(documento)) return true;

  return (
    esDeclaracionDeResidencia(documento) &&
    (documento?.participantes?.length ?? 0) > 0
  );
};

/** Los que se diligencian en línea y no se suben como archivo. */
export const FORMULARIOS_EN_LINEA = [
  DECLARACION_RESIDENCIA,
  RELACION_PARTICIPANTES,
  CARTA_CONFORMACION,
  DECLARACION_RESIDENCIA_Y_CONFORMACION,
];

export const esFormularioEnLinea = (documento: any): boolean =>
  FORMULARIOS_EN_LINEA.some((nombre) => seLlama(documento, nombre));
